import React, { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import { makeStyles } from '@material-ui/core/styles'
import { CircularProgress } from '@material-ui/core'
import { Icon } from '@iconify/react'
import { track } from '@amplitude/analytics-browser'
import { Button, colors } from 'src/components/app-ui'
import UserType from 'src/model/user-types'
import WorkspaceUpgradeContent from 'src/components/workspace-upgrade-popup/WorkspaceUpgradeContent'
import {
  listWorkspaceMembersService,
  inviteWorkspaceMemberService,
  updateWorkspaceMemberPermissionsService,
  deleteWorkspaceMemberService,
  revokeWorkspaceInvitationService,
  listWorkspacePermissionPresetsService,
} from 'src/api/workspace-member'
import { getShopIdFromToken } from 'src/utils/workspace-helper'
import MembersTable from './members-table'

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    color: colors.navy,
  },
  head: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    gap: '16px',
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: 700,
    letterSpacing: '-0.02em',
    lineHeight: 1.25,
    color: colors.navy,
  },
  subtitle: {
    margin: '4px 0 0',
    fontSize: '14px',
    color: colors.muted,
  },
  center: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '80px 16px',
    textAlign: 'center',
    color: colors.muted,
  },
  backdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 1300,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    background: 'rgba(0,0,0,.48)',
  },
  dialog: {
    width: '100%',
    maxWidth: 440,
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    padding: '22px 24px',
    borderRadius: '16px',
    background: '#FFFFFF',
    boxShadow: '0 20px 40px -4px rgba(145,158,171,.24)',
    fontFamily: "'Inter', system-ui, sans-serif",
    color: colors.navy,
  },
  dialogHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dialogTitle: {
    fontSize: '18px',
    fontWeight: 600,
  },
  close: {
    display: 'flex',
    padding: 4,
    border: 0,
    background: 'transparent',
    cursor: 'pointer',
    color: colors.muted,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    fontSize: '13px',
    fontWeight: 500,
    color: colors.muted,
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '10px 12px',
    border: '1px solid rgba(145,158,171,.32)',
    borderRadius: '8px',
    outline: 'none',
    background: '#FFFFFF',
    fontFamily: 'inherit',
    fontSize: '14px',
    color: colors.navy,
    '&:focus': {
      borderColor: colors.primary,
    },
  },
  selectWrap: {
    position: 'relative',
    display: 'flex',
  },
  select: {
    appearance: 'none',
    WebkitAppearance: 'none',
    paddingRight: 36,
    cursor: 'pointer',
  },
  chevron: {
    position: 'absolute',
    top: '50%',
    right: 12,
    transform: 'translateY(-50%)',
    display: 'inline-flex',
    pointerEvents: 'none',
    color: colors.muted,
  },
  error: {
    fontSize: '12px',
    color: '#C9383B',
  },
  actions: {
    marginTop: 4,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
  },
  textButton: {
    padding: '9px 14px',
    border: 0,
    borderRadius: '8px',
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: 500,
    color: colors.muted,
  },
  primaryButton: {
    padding: '9px 16px',
    border: 0,
    borderRadius: '8px',
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: 500,
    color: '#FFFFFF',
  },
  dangerButton: {
    padding: '9px 16px',
    border: 0,
    borderRadius: '8px',
    background: colors.error,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: 500,
    color: '#FFFFFF',
    '&:hover': {
      background: '#C9383B',
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'default',
    },
  },
}))

const isValidEmail = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
const getErrorType = err => err?.type || err?.name || 'UnknownError'

const Dialog = ({ onClose, children }) => {
  const classes = useStyles()
  return createPortal(
    <div className={classes.backdrop} onClick={onClose}>
      <div
        className={classes.dialog}
        role="dialog"
        aria-modal="true"
        onClick={event => event.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body,
  )
}

/**
 * Workspace › Members: invite members, change their role and remove them.
 * `head` is the tab's title/subtitle; this tab renders its own head because
 * the Invite member button sits next to it.
 */
const WorkspaceMembersPage = ({ head }) => {
  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()
  const me = useSelector(state => state.authentication?.me)
  const [shopId, setShopId] = useState(null)
  const [members, setMembers] = useState([])
  const [presets, setPresets] = useState([])
  const [loading, setLoading] = useState(true)
  const [forbidden, setForbidden] = useState(false)
  const [savingId, setSavingId] = useState(null)

  const [invite, setInvite] = useState(null) // { email, permissions }
  const [inviteLoading, setInviteLoading] = useState(false)
  const [removing, setRemoving] = useState(null)
  const [removeLoading, setRemoveLoading] = useState(false)

  const notify = (message, variant = 'success') =>
    enqueueSnackbar(message, { variant })

  const roleOptions = presets.filter(preset => preset.canAssign)

  const getPermissionLabel = resolvedPermissions => {
    if (resolvedPermissions === 0) return 'None'
    const preset = presets.find(p => p.permissions === resolvedPermissions)
    return preset ? preset.name : 'Custom'
  }

  const fetchMembers = useCallback(
    async sid => {
      setLoading(true)
      try {
        const res = await listWorkspaceMembersService(sid)
        setMembers(res.data || [])
      } catch (err) {
        if (err.type === 'ForbiddenError') setForbidden(true)
        else enqueueSnackbar('Failed to load members', { variant: 'error' })
      } finally {
        setLoading(false)
      }
    },
    [enqueueSnackbar],
  )

  const fetchPresets = useCallback(async sid => {
    try {
      const res = await listWorkspacePermissionPresetsService(sid)
      setPresets(res.data || [])
    } catch (err) {
      if (err.type !== 'ForbiddenError') {
        console.error('Failed to load presets', err)
      }
    }
  }, [])

  useEffect(() => {
    const sid = getShopIdFromToken()
    if (sid) {
      setShopId(sid)
      fetchMembers(sid)
      fetchPresets(sid)
    } else {
      enqueueSnackbar('No workspace found in session', { variant: 'error' })
      setLoading(false)
    }
  }, [enqueueSnackbar, fetchMembers, fetchPresets])

  const openInvite = () => {
    // Editor by default, like the design; otherwise the first assignable role.
    const preferred =
      roleOptions.find(option => /editor/i.test(option.name)) || roleOptions[0]
    setInvite({ email: '', permissions: preferred?.permissions ?? null })
  }

  const email = invite?.email.trim().toLowerCase() || ''
  const emailValid = isValidEmail(email)
  const duplicate =
    emailValid && members.some(member => member.email?.toLowerCase() === email)
  const canInvite = emailValid && !duplicate && !inviteLoading

  const submitInvite = async () => {
    if (!canInvite) return
    setInviteLoading(true)
    const trackProps = {
      shop_id: shopId || null,
      invitee_email: email,
      permission_preset: invite.permissions,
    }
    try {
      await inviteWorkspaceMemberService(shopId, {
        email,
        permissionOverrides: invite.permissions,
      })
      track('workspace_member_invite', trackProps)
      notify('Invitation sent')
      setInvite(null)
      fetchMembers(shopId)
    } catch (err) {
      track('workspace_member_invite', {
        ...trackProps,
        error_type: getErrorType(err),
        error_message: err?.message || null,
      })
      notify('Failed to invite member', 'error')
    } finally {
      setInviteLoading(false)
    }
  }

  const changeRole = async (member, permissions) => {
    const key = member.userId || member.email
    setSavingId(key)
    const trackProps = {
      shop_id: shopId || null,
      member_email: member.email || null,
      member_user_id: member.userId || null,
      old_permission: member.resolvedPermissions ?? null,
      new_permission: permissions,
    }
    try {
      await updateWorkspaceMemberPermissionsService(shopId, member.userId, {
        permissions,
      })
      track('workspace_member_permission_edit', trackProps)
      setMembers(current =>
        current.map(item =>
          item === member
            ? {
                ...item,
                resolvedPermissions: permissions,
                hasPermissionOverride: true,
              }
            : item,
        ),
      )
      notify('Role updated')
    } catch (err) {
      track('workspace_member_permission_edit', {
        ...trackProps,
        error_type: getErrorType(err),
        error_message: err?.message || null,
      })
      notify('Failed to update role', 'error')
    } finally {
      setSavingId(null)
    }
  }

  const confirmRemove = async () => {
    const member = removing
    const pending = member.status === 'Pending'
    setRemoveLoading(true)
    const trackProps = {
      shop_id: shopId || null,
      member_email: member.email || null,
      member_user_id: member.userId || null,
      member_status: member.status || 'Active',
      removal_type: pending ? 'pending_invite' : 'active_member',
    }
    try {
      if (pending) {
        await revokeWorkspaceInvitationService(shopId, member.email)
      } else {
        await deleteWorkspaceMemberService(shopId, member.userId)
      }
      track('workspace_member_remove', trackProps)
      notify(pending ? 'Invitation cancelled' : `${member.email} was removed`)
      setRemoving(null)
      fetchMembers(shopId)
    } catch (err) {
      track('workspace_member_remove', {
        ...trackProps,
        error_type: getErrorType(err),
        error_message: err?.message || null,
      })
      notify('Failed to remove member', 'error')
    } finally {
      setRemoveLoading(false)
    }
  }

  const header = (
    <div className={classes.head}>
      <div>
        <h1 className={classes.title}>{head?.title || 'Members'}</h1>
        {head?.subtitle && <p className={classes.subtitle}>{head.subtitle}</p>}
      </div>
      {!forbidden && UserType.hasTeamSeats(me?.userRoleId) && (
        <Button icon="tabler:plus" onClick={openInvite}>
          Invite member
        </Button>
      )}
    </div>
  )

  if (me && !UserType.hasTeamSeats(me?.userRoleId)) {
    return (
      <div className={classes.root}>
        {header}
        <div style={{ maxWidth: 640, margin: '0 auto', width: '100%' }}>
          <WorkspaceUpgradeContent />
        </div>
      </div>
    )
  }

  if (forbidden) {
    return (
      <div className={classes.root}>
        {header}
        <div className={classes.center}>
          <Icon
            icon="tabler:lock"
            width={48}
            height={48}
            color={colors.subtle}
          />
          <div style={{ fontSize: 18, fontWeight: 600, color: colors.navy }}>
            You don't have access to this page
          </div>
          <div>
            Contact your workspace owner to get the required permissions.
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={classes.root}>
      {header}

      {loading && members.length === 0 ? (
        <div className={classes.center}>
          <CircularProgress size={28} />
        </div>
      ) : (
        <MembersTable
          members={members}
          roleOptions={roleOptions}
          getPermissionLabel={getPermissionLabel}
          savingId={savingId}
          onRoleChange={changeRole}
          onRemove={setRemoving}
        />
      )}

      {invite && (
        <Dialog onClose={() => setInvite(null)}>
          <div className={classes.dialogHead}>
            <div className={classes.dialogTitle}>Invite member</div>
            <button
              type="button"
              className={classes.close}
              onClick={() => setInvite(null)}
            >
              <Icon icon="tabler:x" width={18} height={18} />
            </button>
          </div>
          <div className={classes.field}>
            <label className={classes.field}>
              Email
              <input
                className={classes.input}
                type="email"
                autoFocus
                value={invite.email}
                placeholder="name@company.com"
                onChange={event =>
                  setInvite({ ...invite, email: event.target.value })
                }
                onKeyDown={event => event.key === 'Enter' && submitInvite()}
              />
            </label>
            {invite.email.length > 0 && !emailValid && (
              <div className={classes.error}>
                Please enter a valid email address
              </div>
            )}
            {duplicate && (
              <div className={classes.error}>
                This person is already a member or has a pending invitation.
              </div>
            )}
          </div>
          <div className={classes.field}>
            Role
            <div className={classes.selectWrap}>
              <select
                className={`${classes.input} ${classes.select}`}
                value={invite.permissions ?? ''}
                onChange={event =>
                  setInvite({
                    ...invite,
                    permissions: Number(event.target.value),
                  })
                }
              >
                {roleOptions.map(option => (
                  <option key={option.name} value={option.permissions}>
                    {option.name}
                  </option>
                ))}
              </select>
              <span className={classes.chevron}>
                <Icon icon="tabler:chevron-down" width={14} height={14} />
              </span>
            </div>
          </div>
          <div className={classes.actions}>
            <button
              type="button"
              className={classes.textButton}
              onClick={() => setInvite(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className={classes.primaryButton}
              style={{
                background: canInvite ? colors.primary : '#99BFFD',
                cursor: canInvite ? 'pointer' : 'default',
              }}
              disabled={!canInvite}
              onClick={submitInvite}
            >
              {inviteLoading ? 'Inviting…' : 'Invite'}
            </button>
          </div>
        </Dialog>
      )}

      {removing && (
        <Dialog onClose={() => setRemoving(null)}>
          <div className={classes.dialogTitle}>
            {removing.status === 'Pending'
              ? `Cancel the invitation for ${removing.email}?`
              : `Remove ${removing.email} from this workspace?`}
          </div>
          <div className={classes.actions}>
            <button
              type="button"
              className={classes.textButton}
              onClick={() => setRemoving(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className={classes.dangerButton}
              disabled={removeLoading}
              onClick={confirmRemove}
            >
              {removing.status === 'Pending' ? 'Cancel invitation' : 'Remove'}
            </button>
          </div>
        </Dialog>
      )}
    </div>
  )
}

export default WorkspaceMembersPage
